/**
 * Evidence about who else has been editing our DOM.
 *
 * Browser page translation rewrites text runs into `<font>` wrappers, reparenting nodes React
 * owns. React then fails on the next structural edit to that parent — "removeChild ... not a
 * child of this node", or its `insertBefore` twin — and the error boundary takes the app down
 * with it. The same signature shows up across every surface we ship: the booking widget
 * (PRODUCTION-J2 on Chromium, J9 on WebKit), the HostApp reservation details pane (D8, plus DP
 * for insertBefore) and the backoffice.
 *
 * Each app now opts its subtree out with `translate="no"`, but that fixes one *suspected*
 * cause: any injected script that reparents nodes looks identical from React's side. These tags
 * settle it from real traffic. If `dom_translated:true` tracks the crashes, the diagnosis holds
 * and the opt-out should end them; if crashes persist with `dom_translated:false`, something
 * else is rewriting the DOM and `page_lang` / `nav_lang` say whose sessions to look at.
 *
 * Kept free of Sentry and env imports so it stays a plain, testable DOM read, and lives in
 * `shared/` so every app's Sentry bootstrap picks it up.
 */

/** What Google Translate stamps on `<html>` once a page has been translated. */
const TRANSLATED_HTML_CLASSES = ['translated-ltr', 'translated-rtl'];

/**
 * The one name for this crash family, so it can be found in Sentry without knowing which
 * wording a given browser used.
 *
 * The same underlying fault — React editing a child list that no longer matches the real DOM —
 * surfaces under at least four different messages across engines and operations, spread over
 * a dozen separate Sentry issues in three apps. Filtering on `dom_mutation_crash:true` finds
 * all of them at once.
 */
export const DOM_MUTATION_CRASH_TAG = 'dom_mutation_crash';

/**
 * Message fragments for the family. Chromium and WebKit word the same DOMException
 * differently, and it fires on both removal and insertion, so match on all of them.
 */
const DOM_MUTATION_MESSAGES = [
  // Chromium, removal — "Failed to execute 'removeChild' on 'Node'"
  "removechild' on 'node'",
  // Chromium, insertion — "Failed to execute 'insertBefore' on 'Node'"
  "insertbefore' on 'node'",
  // Either operation, wording of the cause
  'is not a child of this node',
  // WebKit's wording for the same DOMException
  'the object can not be found here',
];

/**
 * Does this error belong to the DOM-reparenting crash family?
 *
 * Deliberately message-based rather than fingerprint-based: rewriting Sentry fingerprints
 * would regroup existing issues and lose their history, whereas a tag is additive and leaves
 * the current grouping alone.
 */
export function isDomMutationCrash(message: unknown): boolean {
  if (typeof message !== 'string' || !message) return false;
  const haystack = message.toLowerCase();
  return DOM_MUTATION_MESSAGES.some((needle) => haystack.includes(needle));
}

export interface DomIntegrityTags {
  /** `'true'` when something has rewritten our text nodes. Strings, because Sentry tags are strings. */
  dom_translated: string;
  /** The document's declared language — a mismatch with `nav_lang` is what invites translation. */
  page_lang: string;
  /** The browser's preferred language. */
  nav_lang: string;
}

/**
 * Inspect the live DOM for signs of external rewriting. Never throws: diagnostics must not cost
 * us the error report they are attached to.
 */
export function collectDomIntegrityTags(): Partial<DomIntegrityTags> {
  try {
    if (typeof document === 'undefined') return {};

    const root =
      document.querySelector('.tenseat-widget') ||
      document.getElementById('10seat-booking') ||
      document.getElementById('root');

    const htmlClasses = document.documentElement.className || '';
    const stamped = TRANSLATED_HTML_CLASSES.some((cls) => htmlClasses.split(/\s+/).includes(cls));
    // A <font> inside our own subtree catches translators that do not stamp <html>, and any
    // other script wrapping our text — the widget never renders <font> itself.
    const wrapped = !!root?.querySelector('font');

    return {
      dom_translated: String(stamped || wrapped),
      page_lang: document.documentElement.lang || 'unset',
      nav_lang: (typeof navigator !== 'undefined' && navigator.language) || 'unset',
    };
  } catch {
    return {};
  }
}
